import i18next from 'i18next';
import { find, map } from 'lodash';
import { Fragment } from 'react';

import {
  BalanceChange,
  CopyText,
  CopyTextId,
  DataGridColumnDefinition,
  Money,
} from 'components';
import {
  AssetChangeReason,
  AssetOperationType,
  FilterDefinitionType,
  OrderStatus,
  OrderType,
} from 'enums';
import { AssetCurrencyCode } from 'features/currencies';
import {
  AssetBalanceHistory,
  AssetBalanceHistoryFilters,
  FilterDefinition,
  P2PProviderSummary,
  Shop,
  User,
} from 'types';
import { formatUtils, formUtils, orderUtils } from 'utils';

const ORDER_STATUSES = [
  OrderStatus.Completed,
  OrderStatus.Cancelled,
  OrderStatus.Dispute,
];

const getDefaultFilters = () => ({
  operationTypes: Object.values(AssetOperationType),
  orderStatuses: ORDER_STATUSES,
});

const getOperationTypeLabel = (operationTypes: AssetOperationType) => {
  const { t } = i18next;
  const key = `features.asset_balance_history.operation_type.${operationTypes}`;
  return i18next.exists(key) ? t(key as any) : key;
};

const getOrderTypeLabel = (item: AssetBalanceHistory) => {
  let orderType;

  if (!!item.orderId) {
    orderType = OrderType.Payin;
  } else if (!!item.payoutOrderId) {
    orderType = OrderType.Payout;
  }

  const { t } = i18next;
  const key = `common.${orderType}`;
  return i18next.exists(key) ? t(key as any) : key;
};

const getAssetChangeReasonLabel = (
  reason: AssetChangeReason,
  orderTypeLabel?: string,
) => {
  const { t } = i18next;
  const key = `features.asset_balance_history.reason.${reason}`;
  return i18next.exists(key)
    ? t(key as any, { orderType: orderTypeLabel ? `(${orderTypeLabel})` : '' })
    : key;
};

const getCommonColumns =
  (): DataGridColumnDefinition<AssetBalanceHistory>[] => {
    const { t } = i18next;
    const prefix = 'features.asset_balance_history.columns';
    return [
      {
        header: t(`${prefix}.asset`),
        valueGetter: (item) => <CopyText text={item.assetId} iconOnly />,
      },
      {
        header: t(`${prefix}.date`),
        valueGetter: (item) => formatUtils.formatDate(item.createdAt),
      },
      {
        header: t(`${prefix}.balance`),
        valueGetter: (item) => (
          <span>
            <span className="tw-mr-0.5">
              {formatUtils.formatMoney(item.balance)}
            </span>
            <BalanceChange value={item.balanceChange} />
          </span>
        ),
      },
      {
        header: t(`${prefix}.hold_balance`),
        valueGetter: (item) => (
          <span>
            <span className="tw-mr-0.5">
              {formatUtils.formatMoney(item.holdBalance)}
            </span>
            <BalanceChange value={item.holdBalanceChange} />
          </span>
        ),
      },
      {
        header: t(`${prefix}.currency`),
        valueGetter: (item) => (
          <AssetCurrencyCode id={item.asset?.assetCurrencyId || ''} />
        ),
      },
      {
        header: t(`${prefix}.reason`),
        valueGetter: (item) => {
          const p2pProvider = item.asset?.p2pProvider;
          const order = item.order || item.payoutOrder;
          return (
            <Fragment>
              <div>
                {getAssetChangeReasonLabel(
                  item.assetChangeReason,
                  getOrderTypeLabel(item),
                )}
              </div>
              {order?.currencyRate && (
                <div>
                  <span>{`${t(`${prefix}.currency_rate`)}: `}</span>
                  <span>
                    {p2pProvider && order.p2pProviderOrder?.currencyRate ? (
                      <Money
                        value={order.p2pProviderOrder.currencyRate}
                        fiatCurrencyId={order.fiatCurrencyId}
                        assetCurrencyId={order.p2pProviderOrder.assetCurrencyId}
                        code
                      />
                    ) : (
                      <Money
                        value={order.currencyRate}
                        fiatCurrencyId={order.fiatCurrencyId}
                        assetCurrencyId={order.assetCurrencyId}
                        code
                      />
                    )}
                  </span>
                </div>
              )}
              {item.orderId && (
                <div>
                  <span>{`${t(`${prefix}.order_id`)}: `}</span>
                  <CopyTextId id={item.orderId} />
                </div>
              )}
              {item.payoutOrderId && (
                <div>
                  <span>{`${t(`${prefix}.order_id`)}: `}</span>
                  <CopyTextId id={item.payoutOrderId} />
                </div>
              )}
              {item.fundsRequestId && (
                <div>
                  <span>{`${t(`${prefix}.funds_request_id`)}: `}</span>
                  <CopyTextId id={item.fundsRequestId} />
                </div>
              )}
              {item.internalTransferId && (
                <div>
                  <span>{`${t(`${prefix}.internal_transfer_id`)}: `}</span>
                  <CopyTextId id={item.internalTransferId} />
                </div>
              )}
              {item.assetCurrencyExchangeRequestId && (
                <div>
                  <span>{`${t(
                    `${prefix}.asset_currency_exchange_request_id`,
                  )}: `}</span>
                  <CopyTextId id={item.assetCurrencyExchangeRequestId} />
                </div>
              )}
            </Fragment>
          );
        },
      },
    ];
  };

const getCommonFilters = (): FilterDefinition<AssetBalanceHistoryFilters>[] => {
  const { t } = i18next;
  const prefix = 'features.asset_balance_history.filters';
  return [
    {
      label: t(`${prefix}.order_id`),
      name: 'orderId',
      type: FilterDefinitionType.Text,
      format: 'uuid',
    },
    {
      label: t(`${prefix}.payout_order_id`),
      name: 'payoutOrderId',
      type: FilterDefinitionType.Text,
      format: 'uuid',
    },
    {
      label: t(`${prefix}.asset_id`),
      name: 'assetId',
      type: FilterDefinitionType.Text,
      format: 'uuid',
    },
    {
      label: t(`${prefix}.operation_type`),
      name: 'operationTypes',
      type: FilterDefinitionType.MultiSelect,
      options: map(Object.values(AssetOperationType), (operationType) => ({
        value: operationType,
        label: getOperationTypeLabel(operationType),
      })),
      getDisplayName: (value: AssetOperationType[]) =>
        map(value, (operationType) =>
          getOperationTypeLabel(operationType),
        ).join(', '),
    },
    {
      label: t(`${prefix}.order_status`),
      name: 'orderStatuses',
      type: FilterDefinitionType.MultiSelect,
      options: map(ORDER_STATUSES, (status) => ({
        value: status,
        label: orderUtils.getStatusLabel(status),
      })),
      getDisplayName: (value: OrderStatus[]) =>
        map(value, (status) => orderUtils.getStatusLabel(status)).join(', '),
    },
  ];
};

const getAdminColumns = (): DataGridColumnDefinition<AssetBalanceHistory>[] => {
  const { t } = i18next;
  const prefix = 'features.asset_balance_history.columns';
  return [
    {
      header: t(`${prefix}.user`),
      valueGetter: (item) => (
        <Fragment>
          {item.asset?.user && (
            <Fragment>
              <div>{item.asset.user.name}</div>
              <div>{item.asset.user.email}</div>
            </Fragment>
          )}
          {item.asset?.shop && (
            <div>{formatUtils.formatName(item.asset.shop)}</div>
          )}
          {item.asset?.p2pProvider && <div>{item.asset.p2pProvider.name}</div>}
        </Fragment>
      ),
    },
  ];
};

const getMerchantColumns = (
  shops: Shop[],
): DataGridColumnDefinition<AssetBalanceHistory>[] => {
  const { t } = i18next;
  const prefix = 'features.asset_balance_history.columns';
  return [
    {
      header: t(`${prefix}.shop`),
      valueGetter: (item) => {
        const shop = find(shops, { id: item.asset?.shopId });
        return <div>{shop?.name}</div>;
      },
    },
  ];
};

const getAdminFilters = (
  users: User[],
  p2pProviders: P2PProviderSummary[],
): FilterDefinition<AssetBalanceHistoryFilters>[] => {
  const { t } = i18next;
  const prefix = 'features.asset_balance_history.filters';
  return [
    {
      label: t(`${prefix}.user`),
      name: 'userId',
      type: FilterDefinitionType.User,
      users,
    },
    {
      label: t(`${prefix}.p2p_provider`),
      name: 'p2pProviderId',
      type: FilterDefinitionType.Select,
      options: formUtils.getOptions(p2pProviders),
      getDisplayName: (id: string) => find(p2pProviders, { id })?.name,
    },
  ];
};

const getShopFilters = (
  shops: Shop[],
): FilterDefinition<AssetBalanceHistoryFilters>[] => {
  const { t } = i18next;
  const prefix = 'features.asset_balance_history.filters';
  return [
    {
      label: t(`${prefix}.shop`),
      name: 'shopId',
      type: FilterDefinitionType.Shop,
      shops,
    },
  ];
};

export const assetBalanceHistoryUtils = {
  getCommonColumns,
  getCommonFilters,
  getAdminColumns,
  getTechOperatorColumns: getAdminColumns,
  getAdminFilters,
  getTechOperatorFilters: getAdminFilters,
  getShopFilters,
  getMerchantColumns,
  getAssetChangeReasonLabel,
  getDefaultFilters,
};
